import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Row, Col, Button, Spinner, Card, Carousel, Modal, Alert } from 'react-bootstrap';
import Select, { components } from 'react-select';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ListingMap from './ListingMap'; // Import the ListingMap component
import { FaBed, FaBath, FaCar, FaListUl, FaMap, FaAngleUp, FaQuestionCircle, FaInfoCircle, FaRulerCombined, FaRegCalendarAlt, FaRegClock, FaSlidersH, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { handleTokenAndPostRequest, formatCurrency, trackEvent } from '../utils/utils';
import ValueDisplayGroup from './ValueDisplayGroup';
import SignInModal from './SignInModal'; // Import the SignInModal component
import { computeBranding } from '../utils/useAgentDetails';

// Sticky-filter offset to clear the fixed top nav (px from viewport top).
// Mobile nav is taller (logo + search pill + profile) than desktop.
const STICKY_NAV_OFFSET = 56;
const STICKY_NAV_OFFSET_MOBILE = 58;

// Format the listing's filter_date (e.g. "Fri, 03 Oct 2025 00:00:00 GMT")
// into a short, readable date like "3 Oct 2025".
const formatListingDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Collapse a multi-select's chips into a single "N selected" pill so the
// sticky filter bar keeps a fixed, one-line height regardless of how many
// options are chosen. When only one option is selected we keep the real chip
// so the user can see what they picked at a glance. Falls back to the
// placeholder when nothing is selected.
const CountValueContainer = ({ children, ...props }) => {
    const childArray = React.Children.toArray(children);
    const input = childArray[childArray.length - 1];
    const count = props.getValue().length;
    return (
        <components.ValueContainer {...props}>
            {count > 1
                ? <span className="cp-select-count">{count} selected</span>
                : childArray.slice(0, -1)}
            {input}
        </components.ValueContainer>
    );
};

const dateRangeOptions = [
    { value: '7', label: 'Last week' },
    { value: '30', label: 'Last month' },
    { value: '90', label: 'Last 3 months' },
    { value: '183', label: 'Last 6 months' },
    { value: '365', label: 'Last year' },
    { value: '730', label: 'Last 2 years' },
];

const propertyTypeOptions = [
    { value: 'House', label: 'House' },
    { value: 'Apartment', label: 'Apartment' },
    { value: 'Townhouse', label: 'Townhouse' },
    { value: 'Vacant Land', label: 'Vacant Land' },
    { value: 'Villa', label: 'Villa' },
    { value: 'Acreage Semi Rural', label: 'Acreage Semi Rural' },
    { value: 'Rural', label: 'Rural' },
    { value: 'Semi Detached', label: 'Semi Detached' },
    { value: 'Duplex', label: 'Duplex' },
    { value: 'New House Land', label: 'New House Land' },
    { value: 'New Apartments', label: 'New Apartments' },
    { value: 'Block Of Units', label: 'Block Of Units' },
    { value: 'Studio', label: 'Studio' },
    { value: 'Terrace', label: 'Terrace' }
];

const bedroomOptions = [
    { value: '0', label: '0 bedrooms' },
    { value: '1', label: '1 bedroom' },
    { value: '2', label: '2 bedrooms' },
    { value: '3', label: '3 bedrooms' },
    { value: '4', label: '4 bedrooms' },
    { value: '5', label: '5 bedrooms' },
    { value: '6+', label: '6+ bedrooms' }
];

// 1×1 transparent GIF used as a placeholder src for carousel slides that
// haven't been viewed yet. Prevents the browser from fetching every photo for
// every property card up-front — additional slides only request their real
// image once the user navigates to them.
const BLANK_IMG = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

// Domain's image CDN (rimh*.domainstatic.com.au) aggressively rate-limits
// bursts of requests from a single client, returning 403 once the budget is
// exceeded. These constants tune the retry behaviour for transient failures.
const IMG_MAX_RETRIES = 3;
const IMG_RETRY_BASE_MS = 800;

/**
 * <img> wrapper that automatically retries on load failure with exponential
 * backoff + jitter. Helps mask the 403 throttling responses returned by
 * Domain's CDN when too many requests are made in a short window.
 */
const RetryingImg = ({ src, alt, className, loading }) => {
    // currentSrc is what we actually hand to the browser. We bust the cache on
    // each retry so the browser re-issues a network request instead of
    // replaying the cached 403.
    const [currentSrc, setCurrentSrc] = useState(src);
    const attemptRef = useRef(0);
    const timerRef = useRef(null);

    useEffect(() => {
        attemptRef.current = 0;
        setCurrentSrc(src);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [src]);

    const handleError = () => {
        if (attemptRef.current >= IMG_MAX_RETRIES) return;
        attemptRef.current += 1;
        const delay = IMG_RETRY_BASE_MS * Math.pow(2, attemptRef.current - 1)
            + Math.random() * 400;
        timerRef.current = setTimeout(() => {
            const sep = src.includes('?') ? '&' : '?';
            setCurrentSrc(`${src}${sep}_r=${attemptRef.current}`);
        }, delay);
    };

    return (
        <img
            src={currentSrc}
            alt={alt}
            className={className}
            loading={loading}
            // Some CDN WAF rules reject off-origin referers. Sending none
            // bypasses that check and avoids one class of 403s.
            referrerPolicy="no-referrer"
            onError={handleError}
        />
    );
};

/**
 * Carousel that only loads the active slide's image (plus any previously
 * visited slides). The first slide is loaded eagerly once the carousel scrolls
 * into view; subsequent slides defer their network request until the user
 * clicks next / prev or a pagination indicator.
 *
 * Uses IntersectionObserver to keep cards far below the viewport from issuing
 * any image requests at all (browser-native `loading="lazy"` is too eager and
 * still triggers Domain's CDN rate limiter on long result lists).
 */
const LazyImageCarousel = ({ mediaArray, isMobile }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [loadedIndices, setLoadedIndices] = useState(() => new Set());
    const [inView, setInView] = useState(false);
    const containerRef = useRef(null);

    // Defer the first image request until the card actually scrolls near the
    // viewport. Once visible we stop observing — images stay loaded after that.
    useEffect(() => {
        const node = containerRef.current;
        if (!node || inView) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setInView(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px 0px', threshold: 0.01 });
        observer.observe(node);
        return () => observer.disconnect();
    }, [inView]);

    // Mark slide 0 as loaded as soon as the card becomes visible.
    useEffect(() => {
        if (inView) {
            setLoadedIndices((prev) => (prev.has(0) ? prev : new Set(prev).add(0)));
        }
    }, [inView]);

    const handleSelect = (selectedIndex) => {
        setActiveIndex(selectedIndex);
        setLoadedIndices((prev) => {
            if (prev.has(selectedIndex)) return prev;
            const next = new Set(prev);
            next.add(selectedIndex);
            return next;
        });
    };

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <Carousel
                interval={null}
                indicators={!isMobile}
                controls={!isMobile}
                touch
                activeIndex={activeIndex}
                onSelect={handleSelect}
            >
                {mediaArray.map((mediaItem, mediaIndex) => {
                    const shouldLoad = loadedIndices.has(mediaIndex);
                    return (
                        <Carousel.Item key={mediaIndex}>
                            {shouldLoad ? (
                                <RetryingImg
                                    src={mediaItem.url}
                                    alt={`Slide ${mediaIndex}`}
                                    className="cp-flat-img"
                                    loading={mediaIndex === 0 ? 'eager' : 'lazy'}
                                />
                            ) : (
                                <img
                                    src={BLANK_IMG}
                                    alt=""
                                    className="cp-flat-img"
                                />
                            )}
                        </Carousel.Item>
                    );
                })}
            </Carousel>
        </div>
    );
};

const ComparableProperties = ({ suburb, defaultPropertyType, defaultBedrooms, agentDetails, title, subtitle, modalContent, stickyTopOffset = 0, suburbSearch = null }) => {
    const [activeComparableModeButton, setActiveComparableModeButton] = useState('Sold');
    const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[4]); // Default to last year
    const [comparablesIncludeOnMarket, setComparablesIncludeOnMarket] = useState(false);
    const [comparablesIncludeOffMarket, setComparablesIncludeOffMarket] = useState(true);
    const [comparablesLoading, setComparablesLoading] = useState(false);
    const [recentSoldData, setRecentSoldData] = useState([]);
    const [lowestPrice, setLowestPrice] = useState(null);
    const [showFixedButtonGroup, setShowFixedButtonGroup] = useState(false);
    const [medianPrice, setMedianPrice] = useState(null);
    const [highestPrice, setHighestPrice] = useState(null);
    const [viewMode, setViewMode] = useState('List'); // New state for view mode
    const [excludedProperties, setExcludedProperties] = useState([]); // State for excluded properties
    const [showCard, setShowCard] = useState(false); // State for card visibility
    const [showModal, setShowModal] = useState(false); // State for modal visibility
    const [showScrollToTop, setShowScrollToTop] = useState(false); // State for scroll to top button visibility
    const [comparableCount, setComparableCount] = useState(null);
    const [lowestDom, setLowestDom] = useState(null);
    const [medianDom, setMedianDom] = useState(null);
    const [highestDom, setHighestDom] = useState(null);
    const [showAlert, setShowAlert] = useState(false); // <5 comparables warning
    const [showWideRangeAlert, setShowWideRangeAlert] = useState(false); // Range too wide warning
    const [signInModalShow, setSignInModalShow] = useState(false); // State for SignInModal visibility
    const [modalSuccessTrigger, setModalSuccessTrigger] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
    const [filtersStuck, setFiltersStuck] = useState(false); // Pinned-to-top shadow state
    const [filtersHeight, setFiltersHeight] = useState(0); // Measured so the floating card can sit below
    const [showFilterModal, setShowFilterModal] = useState(false); // Mobile filters modal
    const filtersRef = useRef(null);

    const [selectedPropertyTypes, setSelectedPropertyTypes] = useState(defaultPropertyType ? [{ value: defaultPropertyType, label: defaultPropertyType }] : []);
    const [selectedBedrooms, setSelectedBedrooms] = useState(defaultBedrooms ? [{ value: defaultBedrooms === 6 ? '6+' : defaultBedrooms.toString(), label: defaultBedrooms === 1 ? '1 bedroom' : `${defaultBedrooms} bedrooms` }] : []);
    const handleComparableModeButtonClick = (mode) => {
        trackEvent('click_comparable_listing_type', { mode, suburb, agent_slug: agentDetails?.landing_page_slug });
        const jwtToken = localStorage.getItem('jwt_token');
        const paramToken = localStorage.getItem('param_token');
        if (jwtToken || paramToken) {
            setActiveComparableModeButton(mode);
        } else {
            setSignInModalShow(true);
        }
    };

    const handleViewModeToggle = (mode) => {
        trackEvent('click_comparable_view_mode', { mode, suburb, agent_slug: agentDetails?.landing_page_slug });
        setViewMode(mode);
    };

    const calculatePriceStats = (properties) => {
        const included = properties.filter(property => !excludedProperties.includes(property.id));
        const prices = included
            .map(property => property.price)
            .filter(price => price !== null && price !== undefined);
        const doms = included
            .map(property => property.days_on_market)
            .filter(d => d !== null && d !== undefined);

        let lowestPrice = null, medianPrice = null, highestPrice = null;
        if (prices.length > 0) {
            const sortedPrices = prices.sort((a, b) => a - b);
            medianPrice = sortedPrices.length % 2 === 0
                ? (sortedPrices[sortedPrices.length / 2 - 1] + sortedPrices[sortedPrices.length / 2]) / 2
                : sortedPrices[Math.floor(sortedPrices.length / 2)];
            lowestPrice = sortedPrices[0];
            highestPrice = sortedPrices[sortedPrices.length - 1];
        }

        let lowestDom = null, medianDom = null, highestDom = null;
        if (doms.length > 0) {
            const sortedDoms = doms.sort((a, b) => a - b);
            medianDom = sortedDoms.length % 2 === 0
                ? Math.round((sortedDoms[sortedDoms.length / 2 - 1] + sortedDoms[sortedDoms.length / 2]) / 2)
                : sortedDoms[Math.floor(sortedDoms.length / 2)];
            lowestDom = sortedDoms[0];
            highestDom = sortedDoms[sortedDoms.length - 1];
        }

        return { lowestPrice, medianPrice, highestPrice, lowestDom, medianDom, highestDom, count: prices.length };
    };
    useEffect(() => {
        console.log('useEffect');
        const fetchData = async () => {
            try {
                setComparablesLoading(true);
                console.log('fetchData');
                console.log(selectedPropertyTypes);
                console.log(selectedBedrooms);
    
                const propertyTypes = selectedPropertyTypes ? selectedPropertyTypes.map(option => option.value) : [];
                const bedrooms = selectedBedrooms ? selectedBedrooms.map(option => option.value) : [];
                console.log(propertyTypes);
                console.log(bedrooms);
                const response = await handleTokenAndPostRequest(
                    `${window.api_host}/listing-search`,
                    {
                        suburb: suburb,
                        agent_slug: agentDetails.landing_page_slug,
                        listing_type: activeComparableModeButton,
                        days_in_past: selectedDateRange?.value,
                        include_on_market: comparablesIncludeOnMarket,
                        include_off_market: comparablesIncludeOffMarket,
                        excluded_properties: excludedProperties,
                        property_types: propertyTypes,
                        bedrooms: bedrooms,
                    },
                    {
                        'Content-Type': 'application/json',
                    }
                );
                console.log(response);
                const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
                setRecentSoldData(data['results']);
    
                // Remove any properties from excludedProperties which are not in the results
                const resultIds = data['results'].map(property => property.id);
                setExcludedProperties(prevState => prevState.filter(id => resultIds.includes(id)));
    
                const { lowestPrice, medianPrice, highestPrice, lowestDom, medianDom, highestDom, count } = calculatePriceStats(data['results']);
                setLowestPrice(lowestPrice);
                setMedianPrice(medianPrice);
                setHighestPrice(highestPrice);
                setLowestDom(lowestDom);
                setMedianDom(medianDom);
                setHighestDom(highestDom);
                setComparableCount(count);
            } catch (error) {
                console.error("Error fetching suburb data", error);
            } finally {
                setComparablesLoading(false);
            }
        };
    
        if (suburb && activeComparableModeButton && selectedDateRange && agentDetails) {
            fetchData();
        }
    }, [
        suburb, 
        activeComparableModeButton, 
        selectedDateRange, 
        comparablesIncludeOnMarket, 
        comparablesIncludeOffMarket, 
        agentDetails,
        modalSuccessTrigger, // Add this dependency to trigger the useEffect when modal is successfully closed
        selectedPropertyTypes,
        selectedBedrooms
    ]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1200);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);    


    useEffect(() => {
        const { lowestPrice, medianPrice, highestPrice, lowestDom, medianDom, highestDom, count } = calculatePriceStats(recentSoldData);
        setLowestPrice(lowestPrice);
        setMedianPrice(medianPrice);
        setHighestPrice(highestPrice);
        setLowestDom(lowestDom);
        setMedianDom(medianDom);
        setHighestDom(highestDom);
        setComparableCount(count);
        setShowAlert(count < 5 && count > 0); // Show alert if less than 5 comparables
        // Show a "refine your filters" alert when the spread between low and
        // high is more than 50% of the median — the estimate is too imprecise
        // to be useful and the user should narrow filters to properties more
        // like their own.
        setShowWideRangeAlert(
            count >= 2
            && medianPrice > 0
            && (highestPrice - lowestPrice) / medianPrice > 0.5
        );

    }, [
        excludedProperties, 
        recentSoldData, 
        activeComparableModeButton, 
        selectedDateRange, 
        comparablesIncludeOnMarket, 
        comparablesIncludeOffMarket
    ]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 500) {
                setShowScrollToTop(true);
            } else {
                setShowScrollToTop(false);
            }

            // Show card only when scrolled away from the estimate section
            const estimateSection = document.getElementById('estimate-section');
            if (estimateSection && window.scrollY > estimateSection.offsetTop + estimateSection.offsetHeight && estimateSection.offsetTop > 0) {
                setShowCard(true);
            } else {
                setShowCard(false);
            }

            // Show fixed button group when scrolled past the original position
            const buttonGroup = document.getElementById('button-group');
            if (buttonGroup && window.scrollY + window.innerHeight > buttonGroup.offsetTop + buttonGroup.offsetHeight && buttonGroup.offsetTop > 0) {
                setShowFixedButtonGroup(true);
            } else {
                setShowFixedButtonGroup(false);
            }

            // Compact the filters once they reach the sticky offset under the nav.
            if (filtersRef.current) {
                const rect = filtersRef.current.getBoundingClientRect();
                const stickyOffset = isMobile ? STICKY_NAV_OFFSET_MOBILE : STICKY_NAV_OFFSET;
                setFiltersStuck(rect.top <= stickyOffset + 1);
                setFiltersHeight(rect.height);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleCheckboxChange = (propertyId) => {
        trackEvent('click_comparable_exclude', { property_id: propertyId, suburb, agent_slug: agentDetails?.landing_page_slug });
        const jwtToken = localStorage.getItem('jwt_token');
        const paramToken = localStorage.getItem('param_token');
        if (jwtToken || paramToken) {
            setExcludedProperties(prevState =>
                prevState.includes(propertyId) 
                    ? prevState.filter(id => id !== propertyId) 
                    : [...prevState, propertyId]
            );
        } else {
            setSignInModalShow(true);
        }
    };

    const handleModalSuccess = () => {
        setSignInModalShow(false);
        setModalSuccessTrigger(prev => !prev); // Toggle the state to trigger the useEffect
    };

    const renderListView = () => (
        <div className="cp-flat-list">
            {recentSoldData && recentSoldData.map((property, index) => {
                const mediaArray = JSON.parse(property.media);
                const soldDate = formatListingDate(property.filter_date);
                const dateLabel = property.filter_date_label || 'Date';
                return (
                    <Card
                        key={index}
                        className="cp-property-card cp-property-card--flat"
                    >
                        <div className="cp-flat">
                            <div className="cp-flat-media">
                                <LazyImageCarousel mediaArray={mediaArray} isMobile={isMobile} />
                            </div>
                            <div className="cp-flat-body">
                                <div className="cp-flat-top">
                                    <div className="cp-flat-title">{property.display_address_short}, {property.suburb} {property.postcode}</div>
                                    <div className="cp-flat-price">{property.display_price}</div>
                                </div>
                                <div className="cp-flat-specs">
                                    {property.property_type_display && <span className="cp-flat-type">{property.property_type_display}</span>}
                                    <span><FaBed /> {property.bedrooms}</span>
                                    <span><FaBath /> {property.bathrooms || 0}</span>
                                    <span><FaCar /> {property.carspaces || 0}</span>
                                    {property.land_area ? <span><FaRulerCombined /> {property.land_area} m&sup2;</span> : null}
                                    <span><FaRegClock /> {property.days_on_market} days</span>
                                    {soldDate && <span><FaRegCalendarAlt /> {dateLabel}: {soldDate}</span>}
                                </div>
                                <button
                                    type="button"
                                    className={`cp-exclude-btn ${excludedProperties.includes(property.id) ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); handleCheckboxChange(property.id); }}
                                >
                                    {excludedProperties.includes(property.id)
                                        ? <FaCheckSquare className="cp-exclude-icon" />
                                        : <FaRegSquare className="cp-exclude-icon" />}
                                    {isMobile
                                        ? (excludedProperties.includes(property.id) ? 'Excluded' : 'Exclude')
                                        : (excludedProperties.includes(property.id) ? 'Excluded from estimate' : 'Exclude from estimate')}
                                </button>
                            </div>
                        </div>
                    </Card>
                );
            })}
        </div>
    );

    const renderSkeletonCards = () => (
        <div className="cp-flat-list">
            {Array.from({ length: 5 }).map((_, index) => (
                <Card key={index} className="cp-property-card cp-property-card--flat cp-property-card--skeleton">
                    <div className="cp-flat">
                        <div className="cp-flat-media">
                            <Skeleton height={isMobile ? 118 : 160} borderRadius={0} />
                        </div>
                        <div className="cp-flat-body">
                            <Skeleton width="55%" height={14} style={{ marginBottom: 8 }} />
                            <Skeleton width="85%" height={12} style={{ marginBottom: 8 }} />
                            <Skeleton width={120} height={16} />
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );

    const accent = computeBranding(agentDetails)?.accentColor || agentDetails?.agent_colour || '#2563eb';

    // Select styling. When `collapse` is true (desktop sticky bar) the value
    // container stays on one line (chips are summarised by CountValueContainer).
    // In the mobile modal (collapse false) chips wrap freely so many selections
    // are visible. Menus are portaled + height-capped so they never overflow.
    const buildSelectStyles = (collapse) => ({
        menu: (p) => ({ ...p, zIndex: 9998 }),
        // Portal menu must sit above the mobile filters Modal (z-index 100000)
        // so that dropdown options remain clickable inside the modal.
        menuPortal: (p) => ({ ...p, zIndex: 100001 }),
        // Thin, visible scrollbar to hint that the list is scrollable on desktop.
        menuList: (p) => ({
            ...p,
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 transparent',
            '::-webkit-scrollbar': { width: 8 },
            '::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 8 },
            '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94a3b8' },
            '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }),
        control: (p) => ({ ...p, borderRadius: 10, borderColor: '#e5e7eb', minHeight: collapse ? 40 : 46 }),
        valueContainer: (p) => (collapse
            ? { ...p, flexWrap: 'nowrap', overflow: 'hidden' }
            : { ...p, flexWrap: 'wrap' }),
        option: (p, state) => ({
            ...p,
            backgroundColor: state.isSelected ? accent : state.isFocused ? `${accent}14` : '#fff',
            color: state.isSelected ? '#fff' : '#1a1a1a',
            cursor: 'pointer',
        }),
        multiValue: (p) => ({ ...p, backgroundColor: `${accent}14`, borderRadius: 6 }),
        multiValueLabel: (p) => ({ ...p, color: '#1a1a1a' }),
    });

    const requireAuth = (apply) => {
        const jwtToken = localStorage.getItem('jwt_token');
        const paramToken = localStorage.getItem('param_token');
        if (jwtToken || paramToken) {
            apply();
        } else {
            setSignInModalShow(true);
        }
    };

    const renderModeToggle = () => (
        <div className="cp-toggle-group">
            {['Sold', 'Rent'].map((mode) => (
                <button
                    key={mode}
                    className={`cp-toggle-btn ${activeComparableModeButton === mode ? 'active' : ''}`}
                    onClick={() => handleComparableModeButtonClick(mode)}
                    style={activeComparableModeButton === mode ? {
                        backgroundColor: agentDetails.agent_colour,
                        color: agentDetails.agent_button_colour,
                        borderColor: agentDetails.agent_colour,
                    } : {}}
                >
                    {mode}
                </button>
            ))}
        </div>
    );

    const renderDateSelect = (collapse) => (
        <Select
            options={dateRangeOptions}
            placeholder="Date Range"
            menuPortalTarget={document.body}
            menuPlacement="auto"
            maxMenuHeight={240}
            onChange={(selectedOption) => {
                trackEvent('click_comparable_date_range', { value: selectedOption?.value, suburb, agent_slug: agentDetails?.landing_page_slug });
                requireAuth(() => setSelectedDateRange(selectedOption));
            }}
            value={selectedDateRange}
            styles={buildSelectStyles(collapse)}
        />
    );

    const renderPropertyTypesSelect = (collapse) => (
        <Select
            options={propertyTypeOptions}
            isMulti
            placeholder="Property Types"
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            maxMenuHeight={240}
            components={collapse ? { ValueContainer: CountValueContainer } : undefined}
            onChange={(selectedOptions) => {
                trackEvent('click_comparable_property_types', { values: (selectedOptions || []).map(o => o.value), suburb, agent_slug: agentDetails?.landing_page_slug });
                requireAuth(() => setSelectedPropertyTypes(selectedOptions));
            }}
            value={selectedPropertyTypes}
            styles={buildSelectStyles(collapse)}
        />
    );

    const renderBedroomsSelect = (collapse) => (
        <Select
            options={bedroomOptions}
            isMulti
            placeholder="Bedrooms"
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            maxMenuHeight={240}
            components={collapse ? { ValueContainer: CountValueContainer } : undefined}
            onChange={(selectedOptions) => {
                trackEvent('click_comparable_bedrooms', { values: (selectedOptions || []).map(o => o.value), suburb, agent_slug: agentDetails?.landing_page_slug });
                requireAuth(() => setSelectedBedrooms(selectedOptions));
            }}
            value={selectedBedrooms}
            styles={buildSelectStyles(collapse)}
        />
    );

    const activeFilterCount =
        (selectedPropertyTypes?.length || 0) +
        (selectedBedrooms?.length || 0) +
        (selectedDateRange && selectedDateRange.value !== dateRangeOptions[4].value ? 1 : 0);

    return (
        <div style={{ marginBottom: isMobile ? '112px' : '58px' }}>
            <div className="cp-header">
                {title && (
                    <h3 className="cp-title">
                        {title}
                        <button className="cp-info-btn" onClick={() => { trackEvent('click_comparable_info', { suburb, agent_slug: agentDetails?.landing_page_slug }); setShowModal(true); }} style={{ color: agentDetails.agent_button_colour }}>
                            <FaQuestionCircle />
                        </button>
                    </h3>
                )}
                <p className="cp-subtitle">
                    {subtitle}
                    {!title && (
                        <button className="cp-info-btn" onClick={() => { trackEvent('click_comparable_info', { suburb, agent_slug: agentDetails?.landing_page_slug }); setShowModal(true); }} style={{ color: agentDetails.agent_button_colour }}>
                            <FaQuestionCircle />
                        </button>
                    )}
                </p>
            </div>
            {showAlert && (
                <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible className="cp-alert">
                    <FaInfoCircle style={{ marginRight: '8px', marginBottom: '3px' }} /> The price range is based on less than 5 properties, more comparables will provide a more accurate estimate.
                </Alert>
            )}
            {showWideRangeAlert && (
                <Alert variant="warning" onClose={() => setShowWideRangeAlert(false)} dismissible className="cp-alert">
                    <FaInfoCircle style={{ marginRight: '8px', marginBottom: '3px' }} /> The price range is quite wide. Try adding filters (e.g. property type, bedrooms, time period) to focus on properties more similar to yours.
                </Alert>
            )}
            <div
                className={`cp-filters ${filtersStuck ? 'cp-filters--stuck' : ''}`}
                ref={filtersRef}
                style={{ top: isMobile ? STICKY_NAV_OFFSET_MOBILE : STICKY_NAV_OFFSET }}
            >
                {isMobile ? (
                    <>
                        <Row className="g-2 align-items-stretch">
                            <Col xs={6}>{renderModeToggle()}</Col>
                            <Col xs={6}>
                                <button
                                    type="button"
                                    className="cp-filters-btn"
                                    onClick={() => { trackEvent('click_comparable_open_filters', { suburb, agent_slug: agentDetails?.landing_page_slug }); setShowFilterModal(true); }}
                                >
                                    <FaSlidersH style={{ marginRight: 6 }} />
                                    Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
                                </button>
                            </Col>
                        </Row>
                        {/* Inline LOW/MID/HIGH row + meta keeps the estimate visible without
                            adding extra vertical space on mobile. Replaces the separate
                            ValueDisplayGroup block below (which is hidden on mobile). */}
                        {comparablesLoading && recentSoldData && agentDetails ? (
                            <div className="cp-inline-estimate cp-inline-estimate--skeleton">
                                <div className="cp-inline-stats">
                                    {[0, 1, 2].map((i) => (
                                        <div className="cp-inline-stat" key={i}>
                                            <Skeleton width={32} height={10} />
                                            <Skeleton width={i === 1 ? 78 : 64} height={i === 1 ? 22 : 18} />
                                        </div>
                                    ))}
                                </div>
                                <p className="cp-inline-meta"><Skeleton width={180} height={10} /></p>
                            </div>
                        ) : (comparableCount !== 0 && (
                            <div className="cp-inline-estimate">
                                <div className="cp-inline-stats">
                                    <div className="cp-inline-stat">
                                        <span className="cp-inline-label">LOW</span>
                                        <span className="cp-inline-value">{lowestPrice !== null ? formatCurrency(lowestPrice) : '—'}</span>
                                    </div>
                                    <div className="cp-inline-stat">
                                        <span className="cp-inline-label">MID</span>
                                        <span className="cp-inline-value cp-inline-value--mid" style={{ color: computeBranding(agentDetails)?.isLight ? agentDetails.agent_button_colour : '#111827' }}>{medianPrice !== null ? formatCurrency(medianPrice) : '—'}</span>
                                    </div>
                                    <div className="cp-inline-stat">
                                        <span className="cp-inline-label">HIGH</span>
                                        <span className="cp-inline-value">{highestPrice !== null ? formatCurrency(highestPrice) : '—'}</span>
                                    </div>
                                </div>
                                <p className="cp-inline-meta">{comparableCount} comparables ({recentSoldData.length} results, {excludedProperties.length} excluded)</p>
                            </div>
                        ))}
                    </>
                ) : (
                    <Row className="g-2 align-items-center">
                        <Col md={2}>{renderModeToggle()}</Col>
                        <Col md={3}>{renderDateSelect(true)}</Col>
                        <Col md={4}>{renderPropertyTypesSelect(true)}</Col>
                        <Col md={3}>{renderBedroomsSelect(true)}</Col>
                    </Row>
                )}
            </div>
            <div id="estimate-section" className="cp-estimate-section" style={isMobile ? { display: 'none' } : undefined}>
                {comparablesLoading && recentSoldData && agentDetails ? (
                    <div className="cp-estimate-skeleton">
                        <Skeleton width={140} height={14} style={{ marginBottom: 14 }} />
                        <div className="cp-estimate-skeleton-row">
                            {[0, 1, 2].map((i) => (
                                <div className="cp-estimate-skeleton-cell" key={i}>
                                    <Skeleton width={40} height={12} style={{ marginBottom: 8 }} />
                                    <Skeleton width={i === 1 ? 120 : 90} height={i === 1 ? 32 : 26} />
                                </div>
                            ))}
                        </div>
                        <Skeleton width={220} height={12} style={{ marginTop: 12 }} />
                    </div>
                ) : (
                    comparableCount === 0 ? (
                        <div className="cp-no-results">
                            <p>No comparable properties found.</p>
                            <p className="cp-results-meta">({recentSoldData.length} results, {excludedProperties.length} excluded)</p>
                        </div>
                    ) : (
                        <>
                            <ValueDisplayGroup
                                lowerValue={lowestPrice !== null ? formatCurrency(lowestPrice) : ''}
                                midValue={medianPrice !== null ? formatCurrency(medianPrice) : ''}
                                upperValue={highestPrice !== null ? formatCurrency(highestPrice) : ''}
                                midColor={computeBranding(agentDetails)?.isLight ? agentDetails.agent_button_colour : 'black'}
                                listingType={activeComparableModeButton}
                                showTitle={true}
                            />
                            <p className="cp-results-meta" style={{ marginBottom: 5 }}>
                                {comparableCount} comparables ({recentSoldData.length} results, {excludedProperties.length} excluded)
                            </p>
                        </>
                    )
                )}
            </div>
            <div id="button-group" className="cp-view-toggle-wrapper" style={{ display: 'none' }}>
                {comparablesLoading && recentSoldData && agentDetails ? (
                    <></>
                ) : (
                    <div className={`cp-view-toggle ${showFixedButtonGroup ? 'fixed' : ''}`} style={showFixedButtonGroup ? { bottom: isMobile ? 66 : 10 } : {}}>
                        {/* Map view temporarily hidden; ListingMap component retained for future use. */}
                        {['List'].map((mode) => (
                            <button
                                key={mode}
                                className={`cp-toggle-btn ${viewMode === mode ? 'active' : ''}`}
                                onClick={() => handleViewModeToggle(mode)}
                                style={viewMode === mode ? {
                                    backgroundColor: agentDetails.agent_colour,
                                    color: agentDetails.agent_button_colour,
                                    borderColor: agentDetails.agent_colour,
                                } : {}}
                            >
                                {mode === 'List' ? <FaListUl style={{ marginRight: 5 }} /> : <FaMap style={{ marginRight: 5 }} />}
                                {mode}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {comparablesLoading && recentSoldData && agentDetails ? (
                renderSkeletonCards()
            ) : (
                recentSoldData.length === 0 ? (
                    <></>
                ) : (
                    viewMode === 'List' ? renderListView() : <ListingMap listingData={recentSoldData} agentDetails={agentDetails} />
                )
            )}
            {showCard && !isMobile && viewMode === 'List' && !comparablesLoading && (
                <div className="cp-floating-card" style={{ top: `${STICKY_NAV_OFFSET + (filtersHeight || (isMobile ? 96 : 56)) + 10 + stickyTopOffset}px` }}>
                    {lowestPrice === null && medianPrice === null && highestPrice === null ? (
                        <p style={{ textAlign: 'center', marginBottom: 0 }}>No comparable properties found.</p>
                    ) : (
                        <>
                            <div className="cp-floating-stats">
                                <div className="cp-floating-stat">
                                    <span className="cp-floating-label">LOW</span>
                                    <span className="cp-floating-value">{formatCurrency(lowestPrice)}</span>
                                </div>
                                <div className="cp-floating-stat">
                                    <span className="cp-floating-label">MID</span>
                                    <span className="cp-floating-value mid" style={{ color: computeBranding(agentDetails)?.isLight ? agentDetails.agent_button_colour : '#111827' }}>{formatCurrency(medianPrice)}</span>
                                </div>
                                <div className="cp-floating-stat">
                                    <span className="cp-floating-label">HIGH</span>
                                    <span className="cp-floating-value">{formatCurrency(highestPrice)}</span>
                                </div>
                            </div>
                            <p className="cp-floating-meta">{comparableCount} comparables ({recentSoldData.length} results, {excludedProperties.length} excluded)</p>
                        </>
                    )}
                </div>
            )}
            <Modal fullscreen show={showFilterModal} onHide={() => setShowFilterModal(false)} className="cp-filter-modal" backdropClassName="cp-filter-modal-backdrop">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700 }}>Filters</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {suburbSearch && (
                        <div className="cp-filter-modal-field">
                            <label className="cp-filter-modal-label">Suburb</label>
                            {suburbSearch}
                        </div>
                    )}
                    <div className="cp-filter-modal-field">
                        <label className="cp-filter-modal-label">Listing Type</label>
                        {renderModeToggle()}
                    </div>
                    <div className="cp-filter-modal-field">
                        <label className="cp-filter-modal-label">Date Range</label>
                        {renderDateSelect(false)}
                    </div>
                    <div className="cp-filter-modal-field">
                        <label className="cp-filter-modal-label">Property Types</label>
                        {renderPropertyTypesSelect(false)}
                    </div>
                    <div className="cp-filter-modal-field">
                        <label className="cp-filter-modal-label">Bedrooms</label>
                        {renderBedroomsSelect(false)}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setShowFilterModal(false)}
                        style={{
                            backgroundColor: agentDetails.agent_colour,
                            color: agentDetails.agent_button_colour,
                            borderColor: agentDetails.agent_colour,
                            borderRadius: 10,
                            fontWeight: 600,
                            width: '100%',
                        }}
                    >
                        Show {comparableCount ?? recentSoldData.length} results
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal centered show={showModal} onHide={() => setShowModal(false)} className="cp-modal">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700 }}>How Comparable Analysis Works</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalContent}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { trackEvent('click_comparable_info_close', { suburb, agent_slug: agentDetails?.landing_page_slug }); setShowModal(false); }} style={{ borderRadius: 10 }}>Close</Button>
                </Modal.Footer>
            </Modal>
            {showScrollToTop && (
                <button
                    className="cp-scroll-top"
                    onClick={() => { trackEvent('click_comparable_scroll_top', { suburb, agent_slug: agentDetails?.landing_page_slug }); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                    style={{
                        bottom: isMobile ? 66 : 10,
                        backgroundColor: agentDetails.agent_colour,
                        color: agentDetails.agent_button_colour,
                    }}
                >
                    <FaAngleUp />
                </button>
            )}
            {agentDetails && (
                <SignInModal 
                    show={signInModalShow} 
                    onHide={() => setSignInModalShow(false)}
                    onSuccess={handleModalSuccess}
                    agentDetails={agentDetails}
                />
            )}
        </div>
    );
};

export default ComparableProperties;